import { useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  CUSTOM_TIME_RANGE_VALUE,
  defaultCustomRangeParts,
  encodeCustomTimeRange,
  formatTimeRangeDetailForValue,
  formatYmdLabel,
  getAdminTimeRangeOptions,
  getFinanceTimeRangeOptions,
  labelForTimeRange,
  menuValueForTimeRange,
  parseCustomTimeRange,
  parseYmd,
  type CustomTimeRangeParts,
} from '@/lib/timeRangeOptions';

export type AdminTimeRangePreset = 'admin' | 'finance';

type PickerMode = 'list' | 'custom';

function ymdFromLocalDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function localDateFromYmd(ymd: string): Date | null {
  const parts = parseYmd(ymd);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day);
}

function compareYmd(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Shared admin Time filter: Figma dropdown + day presets from {@link timeRangeOptions}.
 * Use `preset="finance"` for AU Financial Year options (Melbourne 1 Jul → 30 Jun);
 * Dashboard / Engagement stay on `preset="admin"` (day ranges only).
 * Custom range opens two calendars (start + end); both days are inclusive (Melbourne).
 * Multi-day trailing presets (Last 7/30/…) are also Melbourne calendar days (TM-3568);
 * Last 24 Hours stays a rolling datetime window.
 */
export function AdminTimeRangePicker({
  value,
  onChange,
  startDate,
  endDate,
  preset = 'admin',
  ariaLabel = 'Change date range',
  className,
  size = 'compact',
}: {
  value: string;
  onChange: (value: string) => void;
  startDate?: string;
  endDate?: string;
  /** `admin` = shared day presets; `finance` adds AU FY (current + prior 3). */
  preset?: AdminTimeRangePreset;
  ariaLabel?: string;
  className?: string;
  size?: 'default' | 'compact';
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<PickerMode>('list');
  const [draft, setDraft] = useState<CustomTimeRangeParts>(() => defaultCustomRangeParts());
  const [startMonth, setStartMonth] = useState(() => {
    const seed = localDateFromYmd(defaultCustomRangeParts().startYmd);
    return startOfMonth(seed ?? new Date());
  });
  const [endMonth, setEndMonth] = useState(() => {
    const seed = localDateFromYmd(defaultCustomRangeParts().endYmdInclusive);
    return startOfMonth(seed ?? new Date());
  });

  const options =
    preset === 'finance' ? getFinanceTimeRangeOptions() : getAdminTimeRangeOptions();
  const menuValue = menuValueForTimeRange(value);
  const compact = size === 'compact';
  const label = labelForTimeRange(value);
  const detail = formatTimeRangeDetailForValue(value, startDate, endDate);

  function openCustomEditor() {
    const existing = parseCustomTimeRange(value) ?? defaultCustomRangeParts();
    setDraft(existing);
    setStartMonth(startOfMonth(localDateFromYmd(existing.startYmd) ?? new Date()));
    setEndMonth(startOfMonth(localDateFromYmd(existing.endYmdInclusive) ?? new Date()));
    setMode('custom');
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setMode('list');
  }

  function selectPreset(optionValue: string) {
    if (optionValue === CUSTOM_TIME_RANGE_VALUE) {
      openCustomEditor();
      return;
    }
    onChange(optionValue);
    setOpen(false);
    setMode('list');
  }

  function onStartDayClick(ymd: string) {
    setDraft((prev) => {
      if (compareYmd(ymd, prev.endYmdInclusive) > 0) {
        return { startYmd: ymd, endYmdInclusive: ymd };
      }
      return { ...prev, startYmd: ymd };
    });
  }

  function onEndDayClick(ymd: string) {
    setDraft((prev) => {
      if (compareYmd(ymd, prev.startYmd) < 0) {
        return { startYmd: ymd, endYmdInclusive: ymd };
      }
      return { ...prev, endYmdInclusive: ymd };
    });
  }

  function applyCustom() {
    if (compareYmd(draft.startYmd, draft.endYmdInclusive) > 0) return;
    if (!parseYmd(draft.startYmd) || !parseYmd(draft.endYmdInclusive)) return;
    onChange(encodeCustomTimeRange(draft.startYmd, draft.endYmdInclusive));
    setOpen(false);
    setMode('list');
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          aria-expanded={open}
          title={`${label} · ${detail}`}
          className={cn(
            'flex w-[280px] items-center justify-between border bg-[#101010] px-3 text-left outline-none transition-colors',
            compact ? 'h-[49px]' : 'h-[62px]',
            open
              ? 'rounded-t-lg border border-b-0 border-[#0194F5]'
              : 'rounded-lg border border-[#272729] hover:border-[#504F4E]',
            className,
          )}
        >
          <span className="min-w-0">
            <span
              className={cn(
                'block truncate font-normal text-white',
                compact ? 'text-sm leading-5' : 'text-base leading-6',
              )}
            >
              {label}
            </span>
            <span className="block truncate text-xs font-light leading-[14px] text-[#D1D5DB]">
              {detail}
            </span>
          </span>
          <ChevronDown
            className={cn(
              'ml-2 shrink-0 text-white transition-transform',
              compact ? 'size-4' : 'size-5',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={0}
        className={cn(
          'rounded-t-none rounded-b-lg border border-t-0 border-[#0194F5] bg-[#101010] p-2 shadow-none data-[state=open]:animate-none',
          mode === 'custom'
            ? 'w-[min(100vw-2rem,560px)]'
            : 'w-[var(--radix-popover-trigger-width)]',
        )}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {mode === 'list' ? (
          <div className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
            {options.map((opt) => {
              const selected = opt.value === menuValue;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={cn(
                    'h-11 w-full rounded px-3 text-left text-base font-normal leading-6 transition-colors',
                    selected
                      ? 'bg-[#0194F5] text-white'
                      : 'text-[#D1D5DB] hover:bg-[#272729] hover:text-white',
                  )}
                  onClick={() => selectPreset(opt.value)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        ) : (
          <CustomRangeCalendars
            draft={draft}
            startMonth={startMonth}
            endMonth={endMonth}
            onStartMonthChange={setStartMonth}
            onEndMonthChange={setEndMonth}
            onStartDayClick={onStartDayClick}
            onEndDayClick={onEndDayClick}
            onBack={() => setMode('list')}
            onApply={applyCustom}
            canApply={
              Boolean(parseYmd(draft.startYmd) && parseYmd(draft.endYmdInclusive)) &&
              compareYmd(draft.startYmd, draft.endYmdInclusive) <= 0
            }
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function CustomRangeCalendars({
  draft,
  startMonth,
  endMonth,
  onStartMonthChange,
  onEndMonthChange,
  onStartDayClick,
  onEndDayClick,
  onBack,
  onApply,
  canApply,
}: {
  draft: CustomTimeRangeParts;
  startMonth: Date;
  endMonth: Date;
  onStartMonthChange: (month: Date) => void;
  onEndMonthChange: (month: Date) => void;
  onStartDayClick: (ymd: string) => void;
  onEndDayClick: (ymd: string) => void;
  onBack: () => void;
  onApply: () => void;
  canApply: boolean;
}) {
  return (
    <div className="space-y-3 p-1">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="rounded px-2 py-1 text-xs text-[#D1D5DB] hover:bg-[#272729] hover:text-white"
          onClick={onBack}
        >
          Back
        </button>
        <p className="text-xs text-muted-foreground">Australia/Melbourne · both days inclusive</p>
      </div>

      <div className="rounded-md border border-[#272729] px-2 py-1.5 text-center text-xs text-white">
        {formatYmdLabel(draft.startYmd)} – {formatYmdLabel(draft.endYmdInclusive)}
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:gap-3">
        <MonthCalendar
          title="Start date"
          visibleMonth={startMonth}
          onVisibleMonthChange={onStartMonthChange}
          selectedYmd={draft.startYmd}
          rangeStartYmd={draft.startYmd}
          rangeEndYmd={draft.endYmdInclusive}
          onDayClick={onStartDayClick}
        />
        <div className="hidden w-px bg-[#272729] sm:block" aria-hidden />
        <MonthCalendar
          title="End date"
          visibleMonth={endMonth}
          onVisibleMonthChange={onEndMonthChange}
          selectedYmd={draft.endYmdInclusive}
          rangeStartYmd={draft.startYmd}
          rangeEndYmd={draft.endYmdInclusive}
          onDayClick={onEndDayClick}
        />
      </div>

      <button
        type="button"
        disabled={!canApply}
        className={cn(
          'h-10 w-full rounded-md text-sm font-medium text-white transition-colors',
          canApply ? 'bg-[#0194F5] hover:bg-[#0184db]' : 'bg-[#272729] text-[#9CA3AF]',
        )}
        onClick={onApply}
      >
        Apply range
      </button>
    </div>
  );
}

function MonthCalendar({
  title,
  visibleMonth,
  onVisibleMonthChange,
  selectedYmd,
  rangeStartYmd,
  rangeEndYmd,
  onDayClick,
}: {
  title: string;
  visibleMonth: Date;
  onVisibleMonthChange: (month: Date) => void;
  selectedYmd: string;
  rangeStartYmd: string;
  rangeEndYmd: string;
  onDayClick: (ymd: string) => void;
}) {
  const weeks = useMemo(() => {
    const monthStart = startOfMonth(visibleMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [visibleMonth]);

  return (
    <div className="min-w-0 flex-1 space-y-2">
      <p className="text-center text-xs font-medium text-white">{title}</p>
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label={`${title}: previous month`}
          className="rounded p-1.5 text-white hover:bg-[#272729]"
          onClick={() => onVisibleMonthChange(subMonths(visibleMonth, 1))}
        >
          <ChevronLeft className="size-4" />
        </button>
        <p className="text-sm font-medium text-white">{format(visibleMonth, 'MMM yyyy')}</p>
        <button
          type="button"
          aria-label={`${title}: next month`}
          className="rounded p-1.5 text-white hover:bg-[#272729]"
          onClick={() => onVisibleMonthChange(addMonths(visibleMonth, 1))}
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-muted-foreground">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
          <span key={day} className="py-1">
            {day}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {weeks.map((day) => {
          const ymd = ymdFromLocalDate(day);
          const inMonth = isSameMonth(day, visibleMonth);
          const isSelected = ymd === selectedYmd;
          const inRange =
            compareYmd(ymd, rangeStartYmd) >= 0 && compareYmd(ymd, rangeEndYmd) <= 0;

          return (
            <button
              key={`${title}-${ymd}`}
              type="button"
              disabled={!inMonth}
              className={cn(
                'flex h-8 items-center justify-center rounded text-sm transition-colors sm:h-9',
                !inMonth && 'invisible',
                inMonth && !inRange && 'text-[#D1D5DB] hover:bg-[#272729] hover:text-white',
                inMonth && inRange && !isSelected && 'bg-[#0194F5]/30 text-white',
                isSelected && 'bg-[#0194F5] text-white',
              )}
              onClick={() => onDayClick(ymd)}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}
